import { createClient } from '@supabase/supabase-js';
import { SSLErrorRecovery, isSSLRelatedError } from '../utils/siteHealth';

// CRITICAL: This is a VITE app - use VITE_ prefix, NOT NEXT_PUBLIC_
// Required environment variables for production:
// VITE_SUPABASE_URL=https://your-project-id.supabase.co
// VITE_SUPABASE_ANON_KEY=eyJ...your-anon-key
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Declare supabase variable at top level
let supabase: any;

// Enhanced validation with detailed logging
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('🔴 CRITICAL: Missing Supabase environment variables at runtime');

  // Create mock client that returns helpful errors
  supabase = {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signInWithPassword: () => Promise.resolve({ 
        data: null, 
        error: { message: 'Missing environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your deployment settings.' } 
      }),
      signUp: () => Promise.resolve({ 
        data: null, 
        error: { message: 'Missing environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your deployment settings.' } 
      }),
      signOut: () => Promise.resolve({ error: null }),
      resetPasswordForEmail: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }),
      verifyOtp: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }),
      updateUser: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }),
      setSession: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
    },
    from: () => ({
      select: () => ({ eq: () => ({ single: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }) }) }),
      insert: () => ({ select: () => ({ single: () => Promise.resolve({ data: null, error: { message: 'Environment variables not configured' } }) }) }),
      update: () => ({ eq: () => Promise.resolve({ error: { message: 'Environment variables not configured' } }) }),
      delete: () => ({ eq: () => Promise.resolve({ error: { message: 'Environment variables not configured' } }) }),
    }),
  };
} else {
  // Create the real Supabase client
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
          storageKey: 'promptos-auth'
        },
      global: {
        headers: {
          'x-client-info': 'promptos-client@1.0.0'
        }
      }
    });
    
  } catch (error) {
    console.error('Error creating Supabase client:', error);
    throw error;
  }
}

// Enhanced connectivity test
export const testSupabaseConnectivity = async (): Promise<{ success: boolean; error?: string; details?: any }> => {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { success: false, error: 'Environment variables missing' };
  }

  try {
    
    // Test session check
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      // If it's a refresh token error, clear it and try again
      if (error.message?.includes('refresh_token') || 
          error.message?.includes('Refresh Token') ||
          error.code === 'refresh_token_not_found') {
        await clearAllAuthData();
        return { success: true };
      }
      
      return {
        success: false,
        error: `Session check failed: ${error.message}`,
        details: error
      };
    }
    
    return { success: true };
    
  } catch (err) {
    
    // If it's a refresh token error, try to clear and return success
    if (err instanceof Error && (
      err.message?.includes('refresh_token_not_found') ||
      err.message?.includes('Invalid Refresh Token')
    )) {
      await clearAllAuthData();
      return { success: true };
    }
    
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown connectivity error',
      details: err
    };
  }
};

// Helper function to clear all auth data
export const clearAllAuthData = async (): Promise<void> => {
  try {
    
    // Sign out from Supabase with error handling
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      // Continue with cleanup even if signOut fails
    }
    
    // Clear localStorage auth keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('supabase') || 
        key.includes('auth') || 
        key.includes('sb-') ||
        key.includes('promptos-auth')
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('Could not remove key:', key, e);
      }
    });
    
    // Clear sessionStorage as well
    try {
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('auth') || key.includes('sb-'))) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (e) {
      console.warn('Could not clear sessionStorage:', e);
    }
    
  } catch (error) {
    console.error('🔴 Error clearing auth data:', error);
    // Don't throw - this function should always complete
  }
};
// Export at top level
export { supabase };

// Helper function for authenticated user ID
export const uid = () => {
  try {
    const { data: { user } } = supabase.auth.getUser();
    return user?.id;
  } catch (error) {
    console.warn('Error getting user ID:', error);
    return null;
  }
};